import React, { useState } from "react";
import {
  PiToggleRightFill,
  PiToggleLeftLight,
} from "react-icons/pi";
import { FiDownload, FiX } from "react-icons/fi";
import { useDeleteUserMutation, useUnblockblockUserMutation } from "../../redux/features/user/userApi";
import { useNavigate } from "react-router-dom";

const Security = ({ data, onToggleStatus, canWrite, id }) => {
  const [selectedBank, setSelectedBank] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [deleteUser] = useDeleteUserMutation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [unblockblockUser] = useUnblockblockUserMutation();
  const [isActiveState, setIsActiveState] = useState(data.isActive);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();

  // Check if current user is admin
  const isAdmin = () => {
    const role = localStorage.getItem("role");
    return role === "admin";
  };

  const HOME_PAGE = "/";
  const bankData = data?.bank_info || [];
  const { kycVerified, tfaVerified } = data;

  const selectedBankInfo = bankData[selectedBank] || {};
  const {
    holder,
    branch,
    accNumber,
    Upi_id,
    ibanCode,
    account_type,
    proof,
    bankname,
  } = selectedBankInfo;

  const details = [
    { field: "Account Holder Name", text: holder },
    { field: "Account Number", text: accNumber },
    { field: "Branch", text: branch },
    { field: "IFSC Code", text: ibanCode },
    { field: "Account Type", text: account_type },
    { field: "Upi id", text: Upi_id },
  ];

  const handleToggle = (key) => {
    onToggleStatus?.(key);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = proof;
    link.download = `${bankname}_proof.jpg`;
    link.click();
  };

  const handleDeleteUser = async () => {
    setIsDeleting(true);
    try {
      await deleteUser(id).unwrap();
      setShowDeleteModal(false);
      setDeleteSuccess(true);
      setTimeout(() => {
        navigate(HOME_PAGE);
      }, 1000);
    } catch (error) {
      setShowDeleteModal(false);
      alert("Failed to delete user: " + (error.data?.message || error.message));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleBlock = async () => {
    if (!canWrite) return;
    setToggleLoading(true);
    try {
      await unblockblockUser({ id });
      setIsActiveState((prev) => !prev);
    } catch (error) {
      alert("Failed to update user status");
    } finally {
      setToggleLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-blue-800 px-6 py-4 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Bank Account Management</h2>
          <p className="text-sm text-gray-300">View and manage bank account details</p>
        </div>

        <div className="p-6">
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Left Column - Bank Details */}
            <div className="lg:w-[60%] space-y-6">
              <div className="bg-gray-50 rounded-md p-4 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Bank Account</label>
                <select
                  className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-900 transition-colors"
                  onChange={(e) => setSelectedBank(e.target.selectedIndex)}
                >
                  {bankData.map((bank, index) => (
                    <option key={index} value={bank.bankname}>{bank.bankname}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {details.map((dt, index) => (
                  <div key={index} className="bg-gray-50 rounded-md p-4 border border-gray-200">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{dt.field}</label>
                    <div className="text-gray-900 font-medium truncate">{dt.text || "—"}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column - Status & Proof */}
            <div className="lg:w-[40%] space-y-6">
              {/* Account Status */}
              <div className="bg-gray-50 rounded-md p-5 border border-gray-200">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="text-base font-medium text-gray-900">Account Status</h3>
                    <p className="text-xs text-gray-500">Toggle to block/unblock user</p>
                  </div>
                  {isActiveState ? (
                    <PiToggleRightFill
                      size={36}
                      className={`text-blue-600 cursor-pointer hover:text-blue-700 transition-colors ${toggleLoading ? 'opacity-50 pointer-events-none' : ''}`}
                      onClick={handleToggleBlock}
                    />
                  ) : (
                    <PiToggleLeftLight
                      size={36}
                      className={`text-gray-400 cursor-pointer hover:text-gray-500 transition-colors ${toggleLoading ? 'opacity-50 pointer-events-none' : ''}`}
                      onClick={handleToggleBlock}
                    />
                  )}
                </div>
                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  isActiveState ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                }`}>
                  {isActiveState ? "Active" : "Blocked"}
                </div>
              </div>

              {/* Bank Proof */}
              <div className="bg-gray-50 rounded-md p-5 border border-gray-200">
                <h3 className="text-base font-medium text-gray-900 mb-3">Bank Verification</h3>
                {proof ? (
                  <div className="space-y-3">
                    <div className="relative group">
                      <img
                        src={proof}
                        alt="Bank Proof"
                        className="w-full h-48 object-contain border border-gray-200 rounded-md cursor-pointer hover:opacity-90 transition-opacity bg-white"
                        onClick={() => setShowModal(true)}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all rounded-md pointer-events-none"></div>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownload}
                      className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <FiDownload className="w-4 h-4" />
                      Download Proof
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-100 rounded-md border border-gray-200">
                    <p className="text-gray-500 text-sm">No proof document available</p>
                  </div>
                )}
              </div>

              {/* User Delete Button - Only show for admin users */}
              {canWrite && isAdmin() && (
                <div className="bg-gray-50 rounded-md p-5 border border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(true)}
                    className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    Delete User
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Bank Document Preview</h3>
              <button
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
                onClick={() => setShowModal(false)}
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 overflow-auto flex-1">
              <img 
                src={proof} 
                alt="Bank Proof Document" 
                className="w-full h-full object-contain rounded" 
              />
            </div>
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={handleDownload}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center gap-2 transition-colors"
              >
                <FiDownload className="w-4 h-4" />
                Download Document
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white rounded-lg max-w-md w-full flex flex-col shadow-xl">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Confirm User Deletion</h3>
              <button
                className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
                onClick={() => setShowDeleteModal(false)}
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-700">Are you sure you want to delete this user? This action cannot be undone.</p>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={isDeleting}
                className={`px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md text-sm font-medium ${
                  isDeleting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Toast */}
      {deleteSuccess && (
        <div className="fixed top-5 right-5 bg-green-500 text-white px-4 py-2 rounded shadow-lg flex items-center gap-2 z-50">
          <span>User deleted successfully.</span>
          <button 
            onClick={() => setDeleteSuccess(false)} 
            className="ml-2 text-white hover:text-gray-200"
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default Security;